import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { Box, TextField, Button, CircularProgress, Typography, MenuItem, Select, FormControl, InputLabel } from '@mui/material';
import useLanguageForm from '../../hooks/useLanguageForm';
import { useQueryClient } from 'react-query';

const proficiencyLevels = [
    'Native',
    'Fluent',
    'Advanced',
    'Intermediate',
    'Basic'
];

const LanguageForm = ({ onClose, id, cvId }) => {
    const queryClient = useQueryClient();
    const { register, handleSubmit, watch, reset, formState: { errors }, setValue } = useForm({
        defaultValues: {
            title: '',
            proficiency: 'Advanced'
        }
    });

    const { isLoading, error, data, saveLanguage } = useLanguageForm(id);

    useEffect(() => {
        if (data) {
            // Map backend field names to form field names
            const mappedData = {
                title: data.title || '',
                proficiency: data.proficiency || 'Advanced'
            };
            reset(mappedData);
        }
    }, [data, reset]);

    const handleFormSubmit = async (formData) => {
        try {
            const backendData = {
                title: formData.title,
                proficiency: formData.proficiency
            };
            
            // Only include cv in the data if cvId is available
            if (cvId) {
                backendData.cv = cvId;
            }
            
            await saveLanguage(backendData);
            // Invalidate and refetch queries - use null if cvId is undefined to match the query key
            await queryClient.invalidateQueries(['languages', cvId || null]);
            await queryClient.refetchQueries(['languages', cvId || null]);
            // Also invalidate all language queries to be safe
            await queryClient.invalidateQueries(['languages']);
            
            // Reset form and close modal
            reset();
            onClose();
        } catch (err) {
            // Error is handled by the hook and displayed in the form
        }
    };

    const handleCancel = () => {
        // Reset form and close modal
        reset();
        onClose();
    };

    if (isLoading) {
        return (
            <Box display="flex" justifyContent="center" p={3}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <form onSubmit={handleSubmit(handleFormSubmit)}>
            <Box className="space-y-4">
                {error && (
                    <Typography color="error" className="mb-4">
                        {error}
                    </Typography>
                )}

                <TextField
                    fullWidth
                    className='mb-2'
                    label="Language"
                    {...register('title', { required: 'Language is required' })}
                    error={!!errors.title}
                    helperText={errors.title?.message}
                />

                <FormControl fullWidth className="mb-2">
                    <InputLabel id="proficiency-label">Proficiency Level</InputLabel>
                    <Select
                        labelId="proficiency-label"
                        id="proficiency"
                        label="Proficiency Level"
                        {...register('proficiency', { required: 'Proficiency level is required' })}
                    >
                        {proficiencyLevels.map((level) => (
                            <MenuItem key={level} value={level}>
                                {level}
                            </MenuItem>
                        ))}
                    </Select>
                    {errors.proficiency && (
                        <Typography color="error" variant="caption" className="mt-1">
                            {errors.proficiency.message}
                        </Typography>
                    )}
                </FormControl>

                <Box display="flex" justifyContent="flex-end" gap={2}>
                    <Button onClick={handleCancel}>
                        Cancel
                    </Button>
                    <Button type="submit" variant="contained" color="primary">
                        {id ? 'Update' : 'Add'} Language
                    </Button>
                </Box>
            </Box>
        </form>
    );
};

export default LanguageForm; 